import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useId } from 'react';

/** Stored value YYYY-MM-DD → display DD/MM/YYYY */
function isoYmdToDisplayDmy(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/** Parse typed DD/MM/YYYY (1–2 digit day/month ok) → YYYY-MM-DD or null if invalid/incomplete. */
function parseDisplayDmyToIso(s) {
  const t = s.trim();
  if (!t) return '';
  const parts = t.split('/').map((p) => p.trim()).filter((p) => p.length > 0);
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  if (!Number.isFinite(year) || year < 1000 || year > 9999) return null;
  if (!Number.isFinite(month) || month < 1 || month > 12) return null;
  if (!Number.isFinite(day) || day < 1 || day > 31) return null;
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  const yyyy = String(year);
  const iso = `${yyyy}-${mm}-${dd}`;
  const dt = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return null;
  if (dt.getFullYear() !== year || dt.getMonth() + 1 !== month || dt.getDate() !== day) return null;
  return iso;
}

/** Strip to digits, cap at 8, insert / after day and month (auto DD/MM/YYYY as you type). */
function formatTypedDigitsToDmy(value) {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

const DateRangePicker = ({ 
  startDate, 
  endDate, 
  onStartDateChange, 
  onEndDateChange, 
  onClear,
  placeholder = "Select date range",
  style = {},
  /** When true, trigger fills parent width (e.g. modal forms); default keeps min width for toolbar use. */
  fullWidth = false,
  /** Popover stacking; set above parent modal z-index when used inside dialogs. */
  dropdownZIndex = 1000,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  /** Keep calendar panel inside the viewport (modal / short screens). */
  const [panelLayout, setPanelLayout] = useState({ openUp: false, maxHeightPx: 360, fixedLeft: -9999, fixedTop: 0, fixedBottom: undefined, triggerWidth: 260 });
  const fieldIds = useId();
  const startFieldId = `${fieldIds}-start`;
  const endFieldId = `${fieldIds}-end`;

  const [draftStart, setDraftStart] = useState(() => isoYmdToDisplayDmy(startDate || '') || '');
  const [draftEnd, setDraftEnd] = useState(() => isoYmdToDisplayDmy(endDate || '') || '');

  useEffect(() => {
    setDraftStart(isoYmdToDisplayDmy(startDate || '') || '');
  }, [startDate]);

  useEffect(() => {
    setDraftEnd(isoYmdToDisplayDmy(endDate || '') || '');
  }, [endDate]);

  const measurePanelLayout = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const margin = 16;
    const below = window.innerHeight - rect.bottom - margin;
    const above = rect.top - margin;
    const minReadable = 200;
    const naturalPanel = 300;
    const openUp = below < naturalPanel && above > below;
    const cap = Math.max(minReadable, Math.min(naturalPanel, openUp ? above : below));
    setPanelLayout({
      openUp,
      maxHeightPx: cap,
      fixedLeft: rect.left,
      fixedTop: openUp ? undefined : rect.bottom + 4,
      fixedBottom: openUp ? (window.innerHeight - rect.top + 4) : undefined,
      triggerWidth: rect.width,
    });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update temp dates when props change
  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    measurePanelLayout();
    const onReposition = () => measurePanelLayout();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isOpen, measurePanelLayout]);

  const formatDate = (date) => {
    if (!date) return '';
    // Handle both Date objects and YYYY-MM-DD strings
    const d = typeof date === 'string' ? new Date(date + 'T00:00:00') : new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days in the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateInRange = (date) => {
    if (!tempStartDate || !tempEndDate) return false;
    const checkDate = new Date(date);
    const start = new Date(tempStartDate + 'T00:00:00');
    const end = new Date(tempEndDate + 'T23:59:59');
    return checkDate >= start && checkDate <= end;
  };

  const isStartDate = (date) => {
    if (!tempStartDate) return false;
    const checkDate = new Date(date);
    const startDate = new Date(tempStartDate + 'T00:00:00');
    return checkDate.toDateString() === startDate.toDateString();
  };

  const isEndDate = (date) => {
    if (!tempEndDate) return false;
    const checkDate = new Date(date);
    const endDate = new Date(tempEndDate + 'T00:00:00');
    return checkDate.toDateString() === endDate.toDateString();
  };

  const handleDateClick = (date) => {
    // Convert Date object to YYYY-MM-DD format for consistency
    const formatDateForFilter = (dateObj) => {
      const d = new Date(dateObj);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    if (!isSelectingEnd) {
      // Selecting start date
      const formattedDate = formatDateForFilter(date);
      setTempStartDate(formattedDate);
      setIsSelectingEnd(true);
    } else {
      // Selecting end date
      if (new Date(date) >= new Date(tempStartDate)) {
        const formattedDate = formatDateForFilter(date);
        setTempEndDate(formattedDate);
        setIsSelectingEnd(false);
        setIsOpen(false);
        
        // Apply the date range with formatted dates
        onStartDateChange(tempStartDate);
        onEndDateChange(formattedDate);
      } else {
        // If end date is before start date, swap them
        const formattedDate = formatDateForFilter(date);
        setTempStartDate(formattedDate);
        setTempEndDate(tempStartDate);
        setIsSelectingEnd(false);
        setIsOpen(false);
        
        onStartDateChange(formattedDate);
        onEndDateChange(tempStartDate);
      }
    }
  };

  const handleClear = () => {
    setTempStartDate('');
    setTempEndDate('');
    setIsSelectingEnd(false);
    onClear();
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const manualInputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '6px 8px',
    borderRadius: 6,
    border: '1px solid var(--branding-app-surface-panel-border)',
    background: 'var(--branding-app-surface-panel)',
    fontSize: 13,
    fontFamily: 'inherit',
    color: 'var(--cc-text-primary)',
    outline: 'none',
  };

  const commitDraftStart = useCallback(() => {
    const v = draftStart.trim();
    if (!v) {
      onStartDateChange('');
      return;
    }
    const iso = parseDisplayDmyToIso(v);
    if (iso === null) {
      setDraftStart(isoYmdToDisplayDmy(startDate || '') || '');
      return;
    }
    onStartDateChange(iso);
    if (endDate && iso > endDate) {
      onEndDateChange(iso);
    }
  }, [draftStart, startDate, endDate, onStartDateChange, onEndDateChange]);

  const commitDraftEnd = useCallback(() => {
    const v = draftEnd.trim();
    if (!v) {
      onEndDateChange('');
      return;
    }
    const iso = parseDisplayDmyToIso(v);
    if (iso === null) {
      setDraftEnd(isoYmdToDisplayDmy(endDate || '') || '');
      return;
    }
    if (startDate && iso < startDate) {
      onStartDateChange(iso);
      onEndDateChange(startDate);
    } else {
      onEndDateChange(iso);
    }
  }, [draftEnd, startDate, endDate, onStartDateChange, onEndDateChange]);

  return (
    <div
      style={{
        ...(fullWidth ? { width: '100%', boxSizing: 'border-box' } : {}),
        ...style,
      }}
      ref={dropdownRef}
    >
      {/* Trigger + popover share an anchor so the panel sits under the button, not under any manual fields */}
      <div style={{ position: 'relative' }}>
        {/* Trigger Button */}
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            boxSizing: 'border-box',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            background: 'var(--cc-card-bg)',
            cursor: 'pointer',
            fontSize: '14px',
            minWidth: fullWidth ? 0 : '200px',
          }}
        >
          <span style={{ color: startDate && endDate ? 'var(--cc-text-primary)' : 'var(--branding-app-surface-panel-hint)' }}>
            {startDate && endDate
              ? `${formatDate(startDate)} - ${formatDate(endDate)}`
              : placeholder}
          </span>
          <span style={{ fontSize: '12px', color: 'var(--cc-text-muted)' }}>▼</span>
        </button>

        {/* Dropdown Calendar */}
        {isOpen && (
        <div
          style={{
            position: 'fixed',
            left: panelLayout.fixedLeft,
            ...(panelLayout.openUp
              ? { bottom: panelLayout.fixedBottom, top: 'auto' }
              : { top: panelLayout.fixedTop, bottom: 'auto' }),
            zIndex: dropdownZIndex,
            background: 'var(--cc-card-bg)',
            border: '1px solid var(--branding-app-surface-panel-border)',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            padding: '10px',
            width: 228,
            maxHeight: panelLayout.maxHeightPx,
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '14px',
                color: 'var(--cc-text-muted)'
              }}
            >
              ‹
            </button>
            <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--cc-text-primary)' }}>{monthName}</span>
            <button
              type="button"
              onClick={() => navigateMonth(1)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '14px',
                color: 'var(--cc-text-muted)'
              }}
            >
              ›
            </button>
          </div>

          {/* Day Names */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '2px',
            marginBottom: '4px'
          }}>
            {['S','M','T','W','T','F','S'].map((day, i) => (
              <div key={i} style={{
                textAlign: 'center',
                fontSize: '10px',
                fontWeight: 700,
                color: 'var(--cc-text-muted)',
                padding: '3px 0'
              }}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '2px'
          }}>
            {days.map((day, index) => (
              <button
                type="button"
                key={index}
                onClick={() => day && handleDateClick(day)}
                disabled={!day}
                style={{
                  width: '100%',
                  height: '28px',
                  borderRadius: '4px',
                  cursor: day ? 'pointer' : 'default',
                  fontSize: '11px',
                  fontWeight: 500,
                  background: day ? (
                    isStartDate(day) ? 'var(--branding-app-primary)' :
                    isEndDate(day) ? 'var(--branding-app-primary)' :
                    isDateInRange(day) ? 'var(--branding-app-primary-soft)' :
                    'var(--cc-card-bg)'
                  ) : 'transparent',
                  color: day ? (
                    isStartDate(day) || isEndDate(day) ? 'var(--branding-app-primary-fg)' :
                    isDateInRange(day) ? 'var(--branding-app-primary-hover)' :
                    'var(--cc-text-secondary)'
                  ) : 'transparent',
                  border: day && isDateInRange(day) && !isStartDate(day) && !isEndDate(day) 
                    ? '1px solid var(--branding-app-primary-ring)' 
                    : 'none'
                }}
              >
                {day ? day.getDate() : ''}
              </button>
            ))}
          </div>

          {/* Quick Preset Buttons */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: '1px solid var(--branding-app-surface-panel-border)'
          }}>
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                const todayStr = today.toISOString().split('T')[0];
                onStartDateChange(todayStr);
                onEndDateChange(todayStr);
                setIsOpen(false);
              }}
              style={{
                background: 'var(--cc-active-bg)',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '11px',
                color: 'var(--cc-text-muted)',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                const startOfWeek = new Date(today);
                startOfWeek.setDate(today.getDate() - today.getDay());
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                
                const startStr = startOfWeek.toISOString().split('T')[0];
                const endStr = endOfWeek.toISOString().split('T')[0];
                
                onStartDateChange(startStr);
                onEndDateChange(endStr);
                setIsOpen(false);
              }}
              style={{
                background: 'var(--cc-active-bg)',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '11px',
                color: 'var(--cc-text-muted)',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              This Week
            </button>
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                
                const startStr = startOfMonth.toISOString().split('T')[0];
                const endStr = endOfMonth.toISOString().split('T')[0];
                
                onStartDateChange(startStr);
                onEndDateChange(endStr);
                setIsOpen(false);
              }}
              style={{
                background: 'var(--cc-active-bg)',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '11px',
                color: 'var(--cc-text-muted)',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              This Month
            </button>
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                const startOfYear = new Date(today.getFullYear(), 0, 1);
                const endOfYear = new Date(today.getFullYear(), 11, 31);
                
                const startStr = startOfYear.toISOString().split('T')[0];
                const endStr = endOfYear.toISOString().split('T')[0];
                
                onStartDateChange(startStr);
                onEndDateChange(endStr);
                setIsOpen(false);
              }}
              style={{
                background: 'var(--cc-active-bg)',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '11px',
                color: 'var(--cc-text-muted)',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              This Year
            </button>
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: '1px solid var(--branding-app-surface-panel-border)'
          }}>
            <button
              type="button"
              onClick={handleClear}
              style={{
                background: 'var(--cc-active-bg)',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                color: 'var(--cc-text-muted)',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
            <div style={{ fontSize: '12px', color: 'var(--cc-text-muted)' }}>
              {isSelectingEnd ? 'Select end date' : 'Select start date'}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Type (DD/MM/YYYY, auto /) or pick — parent still receives YYYY-MM-DD */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: fullWidth ? '1fr 1fr' : 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 8,
          marginTop: 8,
        }}
      >
        <div>
          <label
            htmlFor={startFieldId}
            style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--cc-text-muted)', marginBottom: 4 }}
          >
            Start
          </label>
          <input
            id={startFieldId}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            placeholder="DD/MM/YYYY"
            value={draftStart}
            onChange={(e) => setDraftStart(formatTypedDigitsToDmy(e.target.value))}
            onBlur={commitDraftStart}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitDraftStart();
                e.target.blur();
              }
            }}
            style={manualInputStyle}
          />
        </div>
        <div>
          <label
            htmlFor={endFieldId}
            style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--cc-text-muted)', marginBottom: 4 }}
          >
            End
          </label>
          <input
            id={endFieldId}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            placeholder="DD/MM/YYYY"
            value={draftEnd}
            onChange={(e) => setDraftEnd(formatTypedDigitsToDmy(e.target.value))}
            onBlur={commitDraftEnd}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitDraftEnd();
                e.target.blur();
              }
            }}
            style={manualInputStyle}
          />
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
